import { useEffect, useState, useMemo } from 'react';
import { useStudent } from '../context/StudentContext';
import { wrongQuestionsApi, knowledgePointsApi, aiApi, type WrongQuestion, type Exam } from '../api';
import {
  Plus, Search, ArrowUpDown, Trash2, Edit, X, BookOpen,
  AlertTriangle, CheckCircle, Clock, Brain, Sparkles,
  ChevronDown, Filter, BarChart3, GraduationCap, Target,
  RefreshCw, Play, ArrowLeft, ArrowRight, Eye, EyeOff, Copy, Cpu,
  AlertCircle, Loader2, Check
} from 'lucide-react';
import { OCRCapture } from '../components/OCRCapture';

const ERROR_TYPES = [
  { value: '概念不清', label: '概念不清', desc: '定义、定理、公式适用条件混淆' },
  { value: '知识遗忘', label: '知识遗忘', desc: '压根没学过、没记住、章节断层' },
  { value: '方法不会', label: '方法不会', desc: '想不到题型套路、不会建模' },
  { value: '审题漏条件', label: '审题漏条件', desc: '看错、漏条件、范围忽略' },
  { value: '逻辑不严谨', label: '逻辑不严谨', desc: '推理跳步、因果倒置' },
  { value: '计算推导错', label: '计算推导错', desc: '算错、化简错、步骤跳错' },
  { value: '低级粗心', label: '低级粗心', desc: '誊抄错、单位错、大小写' },
];

const DIFFICULTIES = [
  { value: '基础', label: '基础', color: 'bg-green-100 text-green-700' },
  { value: '中档', label: '中档', color: 'bg-yellow-100 text-yellow-700' },
  { value: '压轴', label: '压轴', color: 'bg-red-100 text-red-700' },
];

const ATTRIBUTION_LEVELS = [
  { value: '能力不足', label: '能力不足', color: 'bg-purple-100 text-purple-700' },
  { value: '熟练度不足', label: '熟练度不足', color: 'bg-blue-100 text-blue-700' },
  { value: '习惯问题', label: '习惯问题', color: 'bg-orange-100 text-orange-700' },
];

const STATUS_COLORS = {
  '未掌握': 'bg-red-100 text-red-700 border-red-200',
  '基本掌握': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '已掌握': 'bg-green-100 text-green-700 border-green-200',
};

const SUBJECT_COLORS: Record<string, string> = {
  '语文': 'bg-red-100 text-red-700',
  '数学': 'bg-orange-100 text-orange-700',
  '英语': 'bg-green-100 text-green-700',
  '物理': 'bg-blue-100 text-blue-700',
  '化学': 'bg-purple-100 text-purple-700',
  '地理': 'bg-cyan-100 text-cyan-700',
};

interface WrongQuestionForm {
  exam_id?: number;
  subject: string;
  exam_name: string;
  question_content: string;
  correct_answer: string;
  student_answer: string;
  error_type: string;
  difficulty: string;
  attribution_level: string;
  knowledge_points: string;
  status: string;
  notes: string;
}

const initialFormData: WrongQuestionForm = {
  subject: '数学',
  exam_name: '',
  question_content: '',
  correct_answer: '',
  student_answer: '',
  error_type: '计算推导错',
  difficulty: '中档',
  attribution_level: '熟练度不足',
  knowledge_points: '[]',
  status: '未掌握',
  notes: '',
};

export default function WrongBook() {
  const { currentStudent } = useStudent();
  const [questions, setQuestions] = useState<WrongQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<WrongQuestionForm>(initialFormData);
  const [availableExams, setAvailableExams] = useState<Exam[]>([]);
  const [subjects, setSubjects] = useState<string[]>(['语文', '数学', '英语', '物理', '化学', '地理']);

  // 搜索、筛选、排序
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState<string>('');
  const [filterErrorType, setFilterErrorType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // AI 状态
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiExtracting, setAiExtracting] = useState(false);

  // OCR 结果确认对话框
  const [showOCRDialog, setShowOCRDialog] = useState(false);
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [ocrSubject, setOcrSubject] = useState('');
  const [ocrExamName, setOcrExamName] = useState('');
  const [importing, setImporting] = useState(false);

  // 统计数据
  const [stats, setStats] = useState<{
    total: number;
    bySubject: Array<{ subject: string; count: number; mastered: number }>;
    byErrorType: Array<{ error_type: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
    weakPoints: Array<{ name: string; subject: string; mastery_level: number; wrong_count: number }>;
  } | null>(null);

  // Tab 切换
  const [activeTab, setActiveTab] = useState<'list' | 'stats' | 'review'>('list');

  // 复习模式状态
  const [reviewMode, setReviewMode] = useState<'knowledge' | 'subject' | 'error' | 'timeline'>('knowledge');
  const [reviewQuestions, setReviewQuestions] = useState<WrongQuestion[]>([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewAnswer, setReviewAnswer] = useState('');

  // 举一反三状态
  const [similarGenerating, setSimilarGenerating] = useState<number | null>(null);
  const [similarQuestions, setSimilarQuestions] = useState<Record<number, Array<{content: string; answer: string}>>>({});
  const [showSimilar, setShowSimilar] = useState<number | null>(null);
  // 答案显示状态
  const [revealedAnswers, setRevealedAnswers] = useState<Record<string, boolean>>({});
  // 学生答题状态
  const [studentAnswers, setStudentAnswers] = useState<Record<string, string>>({});
  // 答题验证结果
  const [answerResults, setAnswerResults] = useState<Record<string, { correct: boolean; message: string }>>({});

  // LLM 配置状态
  const [llmConfigured, setLlmConfigured] = useState<boolean | null>(null);
  const [llmSource, setLlmSource] = useState<string>('');

  useEffect(() => {
    aiApi.configStatus()
      .then(res => {
        setLlmConfigured(res.data.configured);
        setLlmSource(res.data.source || '');
      })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    if (!currentStudent) return;
    loadQuestions();
    loadStats();
    loadAvailableExams();
    loadSubjects();
  }, [currentStudent?.id]);

  const loadSubjects = async () => {
    if (!currentStudent) return;
    try {
      const res = await wrongQuestionsApi.subjects(currentStudent.id);
      if (res.data.data) setSubjects(res.data.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadQuestions = async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const res = await wrongQuestionsApi.list({ student_id: currentStudent.id });
      setQuestions(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!currentStudent) return;
    try {
      const res = await wrongQuestionsApi.stats(currentStudent.id);
      setStats(res.data.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadAvailableExams = async () => {
    if (!currentStudent) return;
    try {
      const res = await wrongQuestionsApi.availableExams(currentStudent.id);
      setAvailableExams(res.data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // 过滤和排序
  const filteredQuestions = useMemo(() => {
    let result = [...questions];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(q =>
        q.question_content.toLowerCase().includes(term) ||
        q.exam_name.toLowerCase().includes(term)
      );
    }

    if (filterSubject) result = result.filter(q => q.subject === filterSubject);
    if (filterErrorType) result = result.filter(q => q.error_type === filterErrorType);
    if (filterStatus) result = result.filter(q => q.status === filterStatus);

    if (sortConfig) {
      result.sort((a, b) => {
        const aVal = (a as any)[sortConfig.key];
        const bVal = (b as any)[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [questions, searchTerm, filterSubject, filterErrorType, filterStatus, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const openAddDialog = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setShowDialog(true);
  };

  const openEditDialog = (q: WrongQuestion) => {
    setEditingId(q.id);
    setFormData({
      exam_id: q.exam_id || undefined,
      subject: q.subject,
      exam_name: q.exam_name,
      question_content: q.question_content,
      correct_answer: q.correct_answer,
      student_answer: q.student_answer,
      error_type: q.error_type,
      difficulty: q.difficulty,
      attribution_level: q.attribution_level,
      knowledge_points: q.knowledge_points,
      status: q.status,
      notes: q.notes,
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!currentStudent) return;
    if (!formData.question_content || !formData.subject) {
      alert('请填写题目内容和学科');
      return;
    }

    try {
      if (editingId) {
        await wrongQuestionsApi.update(editingId, {
          ...formData,
          student_id: currentStudent.id,
        });
      } else {
        await wrongQuestionsApi.create({
          ...formData,
          student_id: currentStudent.id,
        });
      }
      setShowDialog(false);
      loadQuestions();
      loadStats();
    } catch (e) {
      console.error(e);
      alert('保存失败');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除这条错题？')) return;
    try {
      await wrongQuestionsApi.delete(id);
      loadQuestions();
      loadStats();
    } catch (e) {
      console.error(e);
    }
  };

  // AI 分析
  const handleAIAnalyze = async () => {
    if (!currentStudent || !formData.question_content) return;
    setAiAnalyzing(true);
    try {
      const res = await aiApi.analyze(
        currentStudent.id,
        formData.question_content,
        formData.student_answer,
        formData.correct_answer,
        formData.subject
      );
      const analysis = res.data.data;
      if (analysis) {
        setFormData(prev => ({
          ...prev,
          error_type: analysis.error_type || prev.error_type,
          difficulty: analysis.difficulty || prev.difficulty,
          attribution_level: analysis.attribution_level || prev.attribution_level,
        }));
        alert('AI 分析完成，已自动填充错误类型和难度');
      }
    } catch (e) {
      console.error(e);
      alert('AI 分析失败');
    } finally {
      setAiAnalyzing(false);
    }
  };

  // AI 提取知识点
  const handleAIExtract = async () => {
    if (!currentStudent || !formData.question_content) return;
    setAiExtracting(true);
    try {
      const res = await aiApi.extractKnowledge(
        currentStudent.id,
        formData.question_content,
        formData.subject
      );
      const points = res.data.data;
      if (points && points.length > 0) {
        setFormData(prev => ({
          ...prev,
          knowledge_points: JSON.stringify(points),
        }));
        alert(`已提取 ${points.length} 个知识点`);
      }
    } catch (e) {
      console.error(e);
      alert('知识点提取失败');
    } finally {
      setAiExtracting(false);
    }
  };

  const handleOCRText = (text: string) => {
    setFormData(prev => ({
      ...prev,
      question_content: text,
    }));
  };

  // 处理 OCR 结构化结果
  const handleOCRResult = (result: any) => {
    setOcrResult(result);
    setOcrSubject(result.subject || '数学');
    setOcrExamName(result.exam_name || '');
    setShowOCRDialog(true);
  };

  // 批量导入 OCR 识别的错题
  const handleBatchImport = async () => {
    if (!ocrResult?.wrong_questions?.length) return;
    setImporting(true);
    try {
      const response = await wrongQuestionsApi.batchImport({
        student_id: currentStudent?.id ?? 0,
        subject: ocrSubject,
        exam_name: ocrExamName,
        questions: ocrResult.wrong_questions.map((q: any) => ({
          question_number: q.question_number,
          question_content: q.question_content,
          options: q.options,
          student_answer: q.student_answer,
          correct_answer: q.correct_answer,
          knowledge_point: q.knowledge_point,
        })),
      });
      if (response.data.success) {
        alert(response.data.message);
        setShowOCRDialog(false);
        setOcrResult(null);
        // 刷新列表
        loadQuestions();
      }
    } catch (error: any) {
      console.error('批量导入失败:', error);
      alert(error.response?.data?.message || '批量导入失败');
    } finally {
      setImporting(false);
    }
  };

  const getMasteryRate = (subject: string) => {
    const data = stats?.bySubject.find(s => s.subject === subject);
    if (!data || data.count === 0) return 0;
    return Math.round((data.mastered / data.count) * 100);
  };

  // 举一反三：生成相似题
  const handleGenerateSimilar = async (q: WrongQuestion) => {
    if (!currentStudent) return;
    setSimilarGenerating(q.id);
    try {
      const kps = JSON.parse(q.knowledge_points || '[]');
      const kpNames = kps.map((kp: any) => typeof kp === 'string' ? kp : kp.name);
      const res = await aiApi.generateSimilar(
        currentStudent.id,
        q.question_content,
        q.correct_answer,
        q.subject,
        kpNames
      );
      const similar = res.data.data;
      if (similar && similar.length > 0) {
        setSimilarQuestions(prev => ({ ...prev, [q.id]: similar }));
        setShowSimilar(q.id);
      } else {
        alert('未能生成相似题，请重试');
      }
    } catch (e) {
      console.error(e);
      alert('生成相似题失败');
    } finally {
      setSimilarGenerating(null);
    }
  };

  // 开始复习
  const startReview = (mode: 'knowledge' | 'subject' | 'error' | 'timeline') => {
    let filtered: WrongQuestion[] = [];
    const unmastered = questions.filter(q => q.status !== '已掌握');

    switch (mode) {
      case 'knowledge':
        // 按掌握度排序（未掌握的优先）
        filtered = [...unmastered].sort((a, b) => {
          const aMastered = a.status === '已掌握' ? 1 : a.status === '基本掌握' ? 0 : -1;
          const bMastered = b.status === '已掌握' ? 1 : b.status === '基本掌握' ? 0 : -1;
          return aMastered - bMastered;
        });
        break;
      case 'subject':
        // 按学科分组，每科轮流一题
        const subjectGroups: Record<string, WrongQuestion[]> = {};
        unmastered.forEach(q => {
          if (!subjectGroups[q.subject]) subjectGroups[q.subject] = [];
          subjectGroups[q.subject].push(q);
        });
        const maxLen = Math.max(...Object.values(subjectGroups).map(g => g.length));
        for (let i = 0; i < maxLen; i++) {
          Object.values(subjectGroups).forEach(g => {
            if (g[i]) filtered.push(g[i]);
          });
        }
        break;
      case 'error':
        // 按错误类型分组轮换
        const errorGroups: Record<string, WrongQuestion[]> = {};
        unmastered.forEach(q => {
          if (!errorGroups[q.error_type]) errorGroups[q.error_type] = [];
          errorGroups[q.error_type].push(q);
        });
        const maxErrLen = Math.max(...Object.values(errorGroups).map(g => g.length));
        for (let i = 0; i < maxErrLen; i++) {
          Object.values(errorGroups).forEach(g => {
            if (g[i]) filtered.push(g[i]);
          });
        }
        break;
      case 'timeline':
        // 按时间倒序（最近录入优先）
        filtered = [...unmastered].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
    }

    if (filtered.length === 0) {
      alert('暂无需要复习的错题');
      return;
    }

    setReviewMode(mode);
    setReviewQuestions(filtered);
    setCurrentReviewIndex(0);
    setShowAnswer(false);
    setReviewAnswer('');
    setActiveTab('review');
  };

  // 更新复习状态
  const handleReviewUpdate = async (status: '未掌握' | '基本掌握' | '已掌握') => {
    const current = reviewQuestions[currentReviewIndex];
    if (!current) return;

    try {
      await wrongQuestionsApi.review(current.id, status);
      // 更新本地状态
      setQuestions(prev => prev.map(q =>
        q.id === current.id ? { ...q, status, review_count: q.review_count + 1 } : q
      ));

      // 移动到下一题
      if (currentReviewIndex < reviewQuestions.length - 1) {
        setCurrentReviewIndex(prev => prev + 1);
        setShowAnswer(false);
        setReviewAnswer('');
      } else {
        alert('恭喜！已完成所有错题复习！');
        setActiveTab('list');
        loadStats();
      }
    } catch (e) {
      console.error(e);
    }
  };

  // 复制相似题
  const handleCopySimilar = (content: string) => {
    navigator.clipboard.writeText(content);
    alert('已复制到剪贴板');
  };

  // 切换答案显示
  const toggleAnswer = (key: string) => {
    setRevealedAnswers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // 验证答案
  const verifyAnswer = (key: string, studentAnswer: string, correctAnswer: string) => {
    const normalizedStudent = studentAnswer.trim().toLowerCase();
    const normalizedCorrect = correctAnswer.trim().toLowerCase();

    // 直接比较
    if (normalizedStudent === normalizedCorrect) {
      setAnswerResults(prev => ({
        ...prev,
        [key]: { correct: true, message: '回答正确！' }
      }));
      return;
    }

    // 提取选项字母（用于选择题）
    const extractOption = (answer: string) => {
      const match = answer.match(/^[A-Za-z][\.、）)]?\s*(.+)/);
      return match ? match[1].trim().toLowerCase() : answer.toLowerCase();
    };

    const optionStudent = extractOption(studentAnswer);
    const optionCorrect = extractOption(correctAnswer);

    if (optionStudent === optionCorrect) {
      setAnswerResults(prev => ({
        ...prev,
        [key]: { correct: true, message: '回答正确！' }
      }));
      return;
    }

    setAnswerResults(prev => ({
      ...prev,
      [key]: { correct: false, message: `回答错误，正确答案是：${correctAnswer}` }
    }));
  };

  // 处理答题输入
  const handleStudentAnswer = (key: string, answer: string) => {
    setStudentAnswers(prev => ({ ...prev, [key]: answer }));
    // 清除之前的验证结果
    setAnswerResults(prev => {
      const newResults = { ...prev };
      delete newResults[key];
      return newResults;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500 rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">错题本</h1>
              <p className="text-sm text-gray-500">系统化管理错题，AI 智能分析，举一反三</p>
            </div>
          </div>
          <button
            onClick={openAddDialog}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            录入错题
          </button>
        </div>

        {/* Tab 切换 */}
        {llmSource === 'system' && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
            <Cpu className="w-4 h-4 flex-shrink-0" />
            AI 功能使用系统默认配置。可在侧边栏“AI 设置”中配置你自己的 API Key。
          </div>
        )}

        {/* Tab 切换 - 原始 */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'list'
                ? 'bg-white text-indigo-600 shadow'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              错题列表
              <span className="px-2 py-0.5 bg-gray-200 rounded-full text-xs">{filteredQuestions.length}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'stats'
                ? 'bg-white text-indigo-600 shadow'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              统计分析
            </div>
          </button>
          <button
            onClick={() => {
              // 显示复习模式选择
              const mode = window.confirm('选择复习模式：\n\n确定 - 按掌握程度（薄弱优先）\n取消 - 选择其他模式') ? 'knowledge' : null;
              if (mode) startReview(mode);
              else {
                // 简单选择
                const choice = prompt('复习模式：\n1 - 按掌握程度\n2 - 按学科轮换\n3 - 按错误类型\n4 - 按时间倒序\n\n请输入数字：');
                if (choice) {
                  const modes: Record<string, 'knowledge' | 'subject' | 'error' | 'timeline'> = {
                    '1': 'knowledge', '2': 'subject', '3': 'error', '4': 'timeline'
                  };
                  if (modes[choice]) startReview(modes[choice]);
                }
              }
            }}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-green-500 text-white hover:bg-green-600"
          >
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4" />
              开始复习
            </div>
          </button>
        </div>

        {activeTab === 'list' ? (
          <>
            {/* 搜索和筛选 */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="flex gap-3 mb-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜索题目内容..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2 border rounded-lg transition-colors ${
                    showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                </button>
              </div>

              {showFilters && (
                <div className="flex flex-wrap gap-3 pt-3 border-t">
                  <select
                    value={filterSubject}
                    onChange={e => setFilterSubject(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">全部学科</option>
                    {subjects.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <select
                    value={filterErrorType}
                    onChange={e => setFilterErrorType(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">全部错因</option>
                    {ERROR_TYPES.map(et => (
                      <option key={et.value} value={et.value}>{et.label}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="">全部状态</option>
                    <option value="未掌握">未掌握</option>
                    <option value="基本掌握">基本掌握</option>
                    <option value="已掌握">已掌握</option>
                  </select>
                  {(filterSubject || filterErrorType || filterStatus) && (
                    <button
                      onClick={() => {
                        setFilterSubject('');
                        setFilterErrorType('');
                        setFilterStatus('');
                      }}
                      className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                    >
                      清除筛选
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* 错题列表 */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">加载中...</div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">暂无错题</p>
                <button
                  onClick={openAddDialog}
                  className="mt-3 text-indigo-500 hover:text-indigo-600"
                >
                  录入第一条错题
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredQuestions.map(q => {
                  const kps = JSON.parse(q.knowledge_points || '[]');
                  return (
                    <div key={q.id} className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${SUBJECT_COLORS[q.subject] || 'bg-gray-100'}`}>
                            {q.subject}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[q.status as keyof typeof STATUS_COLORS] || ''}`}>
                            {q.status}
                          </span>
                          {ERROR_TYPES.find(et => et.value === q.error_type) && (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                              {ERROR_TYPES.find(et => et.value === q.error_type)?.label}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditDialog(q)}
                            className="p-1.5 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(q.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* 题目内容 */}
                      <div className="mb-3">
                        <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                          {q.question_content}
                        </p>
                      </div>

                      {/* 答案对比 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                        <div className="p-2 bg-red-50 rounded">
                          <span className="text-gray-500">学生答案：</span>
                          <span className="text-red-700 font-medium">{q.student_answer || '（未填写）'}</span>
                        </div>
                        <div className="p-2 bg-green-50 rounded">
                          <span className="text-gray-500">正确答案：</span>
                          <span className="text-green-700 font-medium">{q.correct_answer || '（未填写）'}</span>
                        </div>
                      </div>

                      {/* 知识点标签 */}
                      {kps.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {kps.map((kp: any, i: number) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                              {typeof kp === 'string' ? kp : kp.name}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* 底部信息 */}
                      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t">
                        <span>{q.exam_name || '自由录入'}</span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleGenerateSimilar(q)}
                            disabled={similarGenerating === q.id}
                            className="flex items-center gap-1 text-indigo-500 hover:text-indigo-600 disabled:opacity-50"
                          >
                            {similarGenerating === q.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3" />
                            )}
                            {similarGenerating === q.id ? '生成中...' : '举一反三'}
                          </button>
                          <span>复习 {q.review_count} 次</span>
                          <span>{new Date(q.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* 举一反三相似题 */}
                      {showSimilar === q.id && similarQuestions[q.id] && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-purple-700">举一反三 · 相似题</p>
                            <button
                              onClick={() => setShowSimilar(null)}
                              className="text-purple-400 hover:text-purple-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="space-y-3">
                            {similarQuestions[q.id].map((sq, i) => {
                              const key = `${q.id}-${i}`;
                              const isRevealed = revealedAnswers[key];
                              const studentAnswer = studentAnswers[key] || '';
                              const result = answerResults[key];
                              return (
                                <div key={i} className="p-3 bg-white rounded-lg">
                                  <p className="text-sm text-gray-700 mb-3">{sq.content}</p>

                                  {/* 答题区域 */}
                                  <div className="mb-3">
                                    <label className="text-xs text-gray-500 mb-1 block">你的答案：</label>
                                    <input
                                      type="text"
                                      value={studentAnswer}
                                      onChange={(e) => handleStudentAnswer(key, e.target.value)}
                                      placeholder="输入你的答案"
                                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && studentAnswer.trim()) {
                                          verifyAnswer(key, studentAnswer, sq.answer);
                                        }
                                      }}
                                    />
                                  </div>

                                  {/* 验证结果 */}
                                  {result && (
                                    <div className={`mb-3 p-2 rounded-lg ${result.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      <p className="text-sm font-medium">{result.message}</p>
                                    </div>
                                  )}

                                  {/* 操作按钮 */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => verifyAnswer(key, studentAnswer, sq.answer)}
                                        disabled={!studentAnswer.trim()}
                                        className="text-xs px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        验证答案
                                      </button>
                                      <button
                                        onClick={() => toggleAnswer(key)}
                                        className="text-xs px-3 py-1 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 flex items-center gap-1"
                                      >
                                        {isRevealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                        {isRevealed ? '隐藏答案' : '查看答案'}
                                      </button>
                                    </div>
                                    <button
                                      onClick={() => handleCopySimilar(sq.content)}
                                      className="text-xs text-purple-500 hover:text-purple-700 flex items-center gap-1"
                                    >
                                      <Copy className="w-3 h-3" />
                                      复制题目
                                    </button>
                                  </div>

                                  {/* 答案显示 */}
                                  {isRevealed && (
                                    <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                                      <p className="text-sm text-green-700 font-medium">
                                        正确答案：{sq.answer}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          /* 统计页面 */
          <div className="space-y-4">
            {/* 概览卡片 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{stats?.total || 0}</p>
                    <p className="text-xs text-gray-500">错题总数</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats?.byStatus.find(s => s.status === '未掌握')?.count || 0}
                    </p>
                    <p className="text-xs text-gray-500">未掌握</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats?.byStatus.find(s => s.status === '已掌握')?.count || 0}
                    </p>
                    <p className="text-xs text-gray-500">已掌握</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats?.total ? Math.round((stats.byStatus.find(s => s.status === '已掌握')?.count || 0) / stats.total * 100) : 0}%
                    </p>
                    <p className="text-xs text-gray-500">掌握率</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 学科掌握情况 */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-indigo-500" />
                各学科掌握情况
              </h3>
              <div className="space-y-3">
                {subjects.map(subject => {
                  const data = stats?.bySubject.find(s => s.subject === subject);
                  const count = data?.count || 0;
                  const mastered = data?.mastered || 0;
                  const rate = count > 0 ? Math.round((mastered / count) * 100) : 0;
                  return (
                    <div key={subject} className="flex items-center gap-3">
                      <span className={`w-12 text-sm font-medium ${SUBJECT_COLORS[subject]}`}>{subject}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            rate >= 70 ? 'bg-green-500' : rate >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-20 text-right">
                        {count} 题 / {rate}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 错误类型分布 */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-amber-500" />
                错误类型分布
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ERROR_TYPES.map(et => {
                  const data = stats?.byErrorType.find(e => e.error_type === et.value);
                  const count = data?.count || 0;
                  return (
                    <div key={et.value} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{et.label}</span>
                        <span className="text-lg font-bold text-gray-800">{count}</span>
                      </div>
                      <p className="text-xs text-gray-400">{et.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 薄弱知识点 */}
            {stats?.weakPoints && stats.weakPoints.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-red-500" />
                  薄弱知识点 TOP10
                </h3>
                <div className="space-y-2">
                  {stats.weakPoints.map((kp, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">{kp.name}</p>
                        <p className="text-xs text-gray-400">{kp.subject} · {kp.wrong_count} 次错</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-bold ${
                          kp.mastery_level < 30 ? 'text-red-600' :
                          kp.mastery_level < 60 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {kp.mastery_level}%
                        </span>
                        <p className="text-xs text-gray-400">掌握度</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 复习模式 */}
        {activeTab === 'review' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* 复习进度头 */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveTab('list')}
                    className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h2 className="font-semibold">错题复习</h2>
                    <p className="text-sm text-green-100">
                      {reviewMode === 'knowledge' && '按掌握程度（薄弱优先）'}
                      {reviewMode === 'subject' && '按学科轮换'}
                      {reviewMode === 'error' && '按错误类型'}
                      {reviewMode === 'timeline' && '按时间倒序'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{currentReviewIndex + 1} / {reviewQuestions.length}</p>
                  <p className="text-sm text-green-100">进度</p>
                </div>
              </div>
              {/* 进度条 */}
              <div className="mt-3 bg-white/20 rounded-full h-2">
                <div
                  className="h-2 bg-white rounded-full transition-all"
                  style={{ width: `${((currentReviewIndex + 1) / reviewQuestions.length) * 100}%` }}
                />
              </div>
            </div>

            {/* 当前题目 */}
            {reviewQuestions[currentReviewIndex] && (
              <div className="p-6">
                {(() => {
                  const current = reviewQuestions[currentReviewIndex];
                  const kps = JSON.parse(current.knowledge_points || '[]');
                  return (
                    <div className="space-y-4">
                      {/* 题目信息 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${SUBJECT_COLORS[current.subject]}`}>
                          {current.subject}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[current.status as keyof typeof STATUS_COLORS]}`}>
                          {current.status}
                        </span>
                        <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                          {ERROR_TYPES.find(et => et.value === current.error_type)?.label}
                        </span>
                      </div>

                      {/* 题目内容 */}
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                          {current.question_content}
                        </p>
                      </div>

                      {/* 你的答案输入 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">写下你的答案：</label>
                        <textarea
                          value={reviewAnswer}
                          onChange={e => setReviewAnswer(e.target.value)}
                          placeholder="在这里输入你的答案..."
                          rows={2}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 resize-none"
                        />
                      </div>

                      {/* 查看答案按钮 */}
                      {!showAnswer && (
                        <button
                          onClick={() => setShowAnswer(true)}
                          className="w-full py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors font-medium"
                        >
                          查看正确答案
                        </button>
                      )}

                      {/* 答案对比 */}
                      {showAnswer && (
                        <div className="space-y-3 animate-fade-in">
                          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-sm text-gray-500 mb-1">你的答案：</p>
                            <p className="text-red-700 font-medium">{reviewAnswer || '（未填写）'}</p>
                          </div>
                          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                            <p className="text-sm text-gray-500 mb-1">正确答案：</p>
                            <p className="text-green-700 font-medium">{current.correct_answer}</p>
                          </div>
                          {kps.length > 0 && (
                            <div className="p-4 bg-blue-50 rounded-xl">
                              <p className="text-sm text-gray-500 mb-2">涉及知识点：</p>
                              <div className="flex flex-wrap gap-2">
                                {kps.map((kp: any, i: number) => (
                                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm">
                                    {typeof kp === 'string' ? kp : kp.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* 复习操作 */}
                      {showAnswer && (
                        <div className="pt-4 border-t">
                          <p className="text-sm text-gray-500 mb-3 text-center">这道题现在掌握得怎么样？</p>
                          <div className="grid grid-cols-3 gap-3">
                            <button
                              onClick={() => handleReviewUpdate('未掌握')}
                              className="py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors font-medium"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <RefreshCw className="w-5 h-5" />
                                <span>未掌握</span>
                              </div>
                            </button>
                            <button
                              onClick={() => handleReviewUpdate('基本掌握')}
                              className="py-3 bg-yellow-100 text-yellow-700 rounded-xl hover:bg-yellow-200 transition-colors font-medium"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <Clock className="w-5 h-5" />
                                <span>基本掌握</span>
                              </div>
                            </button>
                            <button
                              onClick={() => handleReviewUpdate('已掌握')}
                              className="py-3 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors font-medium"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <CheckCircle className="w-5 h-5" />
                                <span>已掌握</span>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* 录入/编辑弹窗 */}
        {showDialog && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* 弹窗头部 */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingId ? '编辑错题' : '录入错题'}
                </h2>
                <button onClick={() => setShowDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* OCR 录入 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">题目图片（可选）</label>
                    <span className="text-xs text-gray-400">支持 OCR 自动识别</span>
                  </div>
                  <OCRCapture onResultReady={handleOCRResult} />
                </div>

                {/* 基本信息 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">学科 *</label>
                    <select
                      value={formData.subject}
                      onChange={e => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {subjects.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">来源考试</label>
                    <select
                      value={formData.exam_id || ''}
                      onChange={e => {
                        const examId = e.target.value ? parseInt(e.target.value) : undefined;
                        const exam = availableExams.find(ex => ex.id === examId);
                        setFormData(prev => ({
                          ...prev,
                          exam_id: examId,
                          exam_name: exam?.exam_name || ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">自由录入</option>
                      {availableExams.map(ex => (
                        <option key={ex.id} value={ex.id}>{ex.exam_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 题目内容 */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">题目内容 *</label>
                    <button
                      onClick={handleAIAnalyze}
                      disabled={!formData.question_content || aiAnalyzing}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                    >
                      <Sparkles className="w-3 h-3" />
                      {aiAnalyzing ? 'AI 分析中...' : 'AI 分析'}
                    </button>
                  </div>
                  <textarea
                    value={formData.question_content}
                    onChange={e => setFormData(prev => ({ ...prev, question_content: e.target.value }))}
                    placeholder="输入或通过 OCR 识别题目内容..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>

                {/* 答案 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">学生答案</label>
                    <input
                      type="text"
                      value={formData.student_answer}
                      onChange={e => setFormData(prev => ({ ...prev, student_answer: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">正确答案</label>
                    <input
                      type="text"
                      value={formData.correct_answer}
                      onChange={e => setFormData(prev => ({ ...prev, correct_answer: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* 错误类型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">错误类型</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ERROR_TYPES.map(et => (
                      <button
                        key={et.value}
                        onClick={() => setFormData(prev => ({ ...prev, error_type: et.value }))}
                        className={`p-2 rounded-lg border text-left transition-colors ${
                          formData.error_type === et.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-700">{et.label}</p>
                        <p className="text-xs text-gray-400">{et.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 难度和归因 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">难度</label>
                    <div className="flex gap-2">
                      {DIFFICULTIES.map(d => (
                        <button
                          key={d.value}
                          onClick={() => setFormData(prev => ({ ...prev, difficulty: d.value }))}
                          className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            formData.difficulty === d.value
                              ? d.color
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">归因等级</label>
                    <div className="flex gap-2">
                      {ATTRIBUTION_LEVELS.map(a => (
                        <button
                          key={a.value}
                          onClick={() => setFormData(prev => ({ ...prev, attribution_level: a.value }))}
                          className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            formData.attribution_level === a.value
                              ? a.color
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 知识点 */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">知识点</label>
                    <button
                      onClick={handleAIExtract}
                      disabled={!formData.question_content || aiExtracting}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                    >
                      <Sparkles className="w-3 h-3" />
                      {aiExtracting ? '提取中...' : 'AI 提取'}
                    </button>
                  </div>
                  {formData.knowledge_points && formData.knowledge_points !== '[]' ? (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex flex-wrap gap-2">
                        {JSON.parse(formData.knowledge_points).map((kp: any, i: number) => (
                          <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                            {typeof kp === 'string' ? kp : kp.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="手动输入知识点，用逗号分隔"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => {
                        const points = e.target.value.split(/[,，]/).filter(Boolean);
                        const kps = points.map(p => ({ name: p.trim(), description: '' }));
                        setFormData(prev => ({ ...prev, knowledge_points: JSON.stringify(kps) }));
                      }}
                    />
                  )}
                </div>

                {/* 掌握状态 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">掌握状态</label>
                  <div className="flex gap-2">
                    {['未掌握', '基本掌握', '已掌握'].map(s => (
                      <button
                        key={s}
                        onClick={() => setFormData(prev => ({ ...prev, status: s }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.status === s
                            ? STATUS_COLORS[s as keyof typeof STATUS_COLORS]
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 备注 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="补充说明..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
              </div>

              {/* 弹窗底部 */}
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
                <button
                  onClick={() => setShowDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                >
                  {editingId ? '保存修改' : '录入错题'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* OCR 结果确认对话框 */}
        {showOCRDialog && ocrResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* 对话框头部 */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-800">OCR 识别结果确认</h3>
                </div>
                <button
                  onClick={() => setShowOCRDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* 对话框内容 */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* 科目和试卷名称 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">科目</label>
                    <select
                      value={ocrSubject}
                      onChange={(e) => setOcrSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {subjects.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">试卷名称</label>
                    <input
                      type="text"
                      value={ocrExamName}
                      onChange={(e) => setOcrExamName(e.target.value)}
                      placeholder={ocrResult.exam_name || '请输入试卷名称'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* 识别到的错题列表 */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    识别到的错题（{ocrResult.wrong_questions?.length || 0} 道）
                  </h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {ocrResult.wrong_questions?.map((q: any, idx: number) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {q.question_number || `第${idx + 1}题`}
                          </span>
                          {q.student_answer && (
                            <span className="text-xs text-red-600">
                              学生答案：{q.student_answer}
                            </span>
                          )}
                          {q.correct_answer && (
                            <span className="text-xs text-green-600">
                              正确答案：{q.correct_answer}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700">{q.question_content}</p>
                        {q.options && (
                          <pre className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{q.options}</pre>
                        )}
                        {q.knowledge_point && (
                          <p className="text-xs text-gray-500 mt-1">知识点：{q.knowledge_point}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 非结构化输出警告 */}
                {ocrResult.parse_error && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">AI 返回了非结构化输出</p>
                      <p className="text-xs text-yellow-700 mt-1">请检查识别结果，可能需要手动调整</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 对话框底部按钮 */}
              <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowOCRDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={importing}
                >
                  取消
                </button>
                <button
                  onClick={handleBatchImport}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={importing || !ocrResult.wrong_questions?.length}
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      导入中...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      确认导入（{ocrResult.wrong_questions?.length || 0} 题）
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
